/**
 * Extracted requirements — AI contract and enriched storage shape.
 *
 * An element row puts one material on one building part, with the share of
 * that part, so a customer baseline can set that product on the element.
 * The stored element row is {@link elementRequirementSchema}: `type`,
 * `buildingId`, `place`, `name`, `share`, `sbef`, and a material
 * `constraint`. A document-local name for a portion of that part is not
 * stored. A surface finish is not a material and is not stored.
 * `evidence_excerpt` stays empty. A max building height or a storey count
 * stays a whole-building cap on {@link buildingPartRequirementSchema}
 * (`applies_to: building`). That cap has no element fields. The model emits
 * {@link buildingPartRequirementAiSchema} with a flat constraint (OpenAI
 * structured outputs reject `oneOf`). Server enrich narrows an element row
 * to {@link elementRequirementSchema} and leaves the cap on the stored
 * building schema. SBEF is the code the row names. Enrich does not look it
 * up from a part name. Color / NCS is not representable.
 */

import { z } from 'zod';
import { TYPBETECKNING_CODES } from '../construction/typbeteckningar.data';

const nullableStr = (description: string) =>
  z.nullable(z.string()).describe(description);

const nullableNum = (description: string) =>
  z.nullable(z.number()).describe(description);

export const BUILDING_PART_MATERIALS = [
  'concrete',
  'timber',
  'steel',
  'aluminium',
  'sheet_metal',
  'bitumen_felt',
  'green_roof',
  'glass',
  'brick',
  'gypsum',
  'insulation',
  'other',
] as const;

export type BuildingPartMaterial = (typeof BUILDING_PART_MATERIALS)[number];

export const BUILDING_PART_MATERIAL_LABELS = {
  concrete: 'Betong',
  timber: 'Trä',
  steel: 'Stål',
  aluminium: 'Aluminium',
  sheet_metal: 'Plåt',
  bitumen_felt: 'Papp',
  green_roof: 'Biotoptak',
  glass: 'Glas',
  brick: 'Tegel',
  gypsum: 'Gips',
  insulation: 'Isolering',
  other: 'Övrigt',
} as const satisfies Record<BuildingPartMaterial, string>;

export const buildingPartMaterialSchema = z.enum(BUILDING_PART_MATERIALS);

export const BUILDING_PART_RANGE_PARAMETER_IDS = [
  'thermal_transmittance',
  'building_height',
  'storey_count',
] as const;

export type BuildingPartRangeParameterId =
  (typeof BUILDING_PART_RANGE_PARAMETER_IDS)[number];

export const BUILDING_PART_RANGE_PARAMETER_LABELS = {
  thermal_transmittance: 'U-värde',
  building_height: 'Byggnadshöjd (m)',
  storey_count: 'Våningsantal',
} as const satisfies Record<BuildingPartRangeParameterId, string>;

export const buildingPartRangeParameterIdSchema = z.enum(
  BUILDING_PART_RANGE_PARAMETER_IDS,
);

/** One comparison on a numeric limit. Not a heading synonym. */
export const BUILDING_PART_LIMIT_COMPARISONS = [
  'lt',
  'lte',
  'gt',
  'gte',
  'eq',
] as const;

export type BuildingPartLimitComparison =
  (typeof BUILDING_PART_LIMIT_COMPARISONS)[number];

export const buildingPartLimitComparisonSchema = z.enum(
  BUILDING_PART_LIMIT_COMPARISONS,
);

export const typbeteckningCodeSchema = z.enum(TYPBETECKNING_CODES);

/**
 * Product names a document word can already match. `other` is not a product.
 * A word that matches none of these stays the word.
 */
const genericMaterialProductList = BUILDING_PART_MATERIALS.flatMap((id) =>
  id === 'other' ? [] : [`${BUILDING_PART_MATERIAL_LABELS[id]} (\`${id}\`)`],
).join(', ');

/**
 * Same instruction in the blueprint, brief, and technical-spec prompts, and
 * on the requirements array. One material on one building or one building part.
 */
export const BUILDING_PART_MATERIAL_ROW_INSTRUCTION = `An element row puts one material on one building part, with the share of that part. Facade, concrete, the whole wall, building 1 means the whole external wall of building 1 is concrete. \`type\` is \`element\`. \`buildingId\` is that building. Use that building's classifier id when the list has it. Do not copy a different building's id onto the row. When the document names the building and the listed id is another building, the id is the building the document names, in the same form as a classifier id. \`place\` is which building part. A load-bearing external wall is \`external_wall\`. Roof covering is \`roof\`. The cladding layer outside that wall is not \`external_wall\`; that layer is \`facade_cladding\`. Floor slabs are \`floor_slab\`. A frame the row does not call an external wall is \`frame\`. A facade the row does not call the cladding layer is \`facade\`. \`name\` is the label. When the document word matches one of these Nodon generic products, ignoring letter case, store that product's name and set \`constraint.allowed\` to its id: ${genericMaterialProductList}. A match is that word, not a longer phrase that contains it. The row is then done. A word that does not match stays as the word, and \`allowed\` is empty. Not a product id in \`name\`. Letter case is not a second material. Not a list of materials in one string. Not a surface finish. Not a sentence, not a joined heading, and not a document-local name for a portion of that part. \`share\` is the portion of that part, from 0 to 1. \`1\` is the whole part when the document does not split it. A fraction the document states is that number. When the document splits a part and does not state the portions, \`share\` is null and the row stays. Do not invent a fraction. A count is not a portion. \`sbef\` is the element class. A load-bearing external wall is \`31\`. Roof covering is \`43\`. Facade cladding is \`53\`. Floor slabs are \`34\` when the row says that subclass. When the row only names the family, the code is 3 frame, 4 roof, or 5 facades. Set the second digit only when the row says the subclass: 34 floor slabs, 43 roof covering, 53 facade cladding. A code the document prints is kept. Do not invent a second digit. The same SBEF with different materials stays separate rows. \`applies_to\` is \`building_part\`. \`source_label\` is an empty string. \`building_instance_key\` is null. \`typcode\` is null. \`evidence_excerpt\` is an empty string. Do not quote the document. Drop a row that is only a detail and not one of those parts. Also drop a finish, a document-local portion name, only a number, a sentence, a quote, or a joined heading. The share of a portion, when the document states it, is \`share\`. A max building height or a storey count stays a whole-building cap and is not an element row. \`applies_to\` is \`building\`. \`type\`, \`buildingId\`, \`place\`, \`share\`, and \`sbef\` are null. \`typcode\` is null. \`source_label\` is the subject the document prints. Do not replace that subject. When the document names the building, \`name\` is that building. Otherwise \`name\` is null. \`building_instance_key\` is that building, not another building's id.`;

export const buildingPartPrintedCodesSchema = z
  .object({
    sbef: nullableStr(
      'Null. An element row stores the code on `sbef`. A whole-building cap has no code.',
    ),
    littra: nullableStr('Null. An element row does not store littra.'),
  })
  .strict()
  .describe(
    'Unused on an element row and on a whole-building cap. Both codes are null.',
  );

export const BUILDING_REQUIREMENT_APPLIES_TO = [
  'building_part',
  'building',
] as const;

export type BuildingRequirementAppliesTo =
  (typeof BUILDING_REQUIREMENT_APPLIES_TO)[number];

export const BUILDING_PART_CONSTRAINT_KINDS = [
  'material',
  'range',
  'limit',
  'text',
] as const;

export type BuildingPartConstraintKind =
  (typeof BUILDING_PART_CONSTRAINT_KINDS)[number];

/**
 * OpenAI structured outputs reject `oneOf`, so the model always emits every
 * constraint field. Unused siblings are null (`kind=material` → parameter
 * fields null; `kind=range` → allowed null).
 */
export const buildingPartConstraintAiSchema = z
  .object({
    kind: z
      .enum(BUILDING_PART_CONSTRAINT_KINDS)
      .describe(
        'material = one material on an element row. The label is `name`. The product id, when the word matches, is `allowed`. range = a catalog parameter bound, including a max building height. Do not use limit or text for an element row. A thickness, a width, or any other number that is not a catalog range is not a row. A sentence, a quote, or a joined heading is not a row. Set unused sibling fields to null.',
      ),
    allowed: z
      .nullable(z.array(buildingPartMaterialSchema))
      .describe(
        'The generic product id when `name` is that product. Empty when the document word does not match a product. Null when kind is not material. Never color/NCS.',
      ),
    parameter_id: z
      .nullable(buildingPartRangeParameterIdSchema)
      .describe(
        'Required when kind=range. thermal_transmittance = U-värde (W/(m²K)); building_height = byggnadshöjd/nockhöjd/totalhöjd (m) of the main buildings, measured from the ground; storey_count = våningsantal. A height given above a datum or sea level (över nollplan, +RH2000) is not a building_height and not a row. A cap for another kind of building on the site (komplementbyggnad, förråd) is not a row. Null when kind=material.',
      ),
    min: nullableNum(
      'Inclusive lower bound in catalog unit; null if unbounded or kind=material.',
    ),
    max: nullableNum(
      'Inclusive upper bound in the parameter unit (W/(m²K), metres, or storeys); null if unbounded or kind is not range.',
    ),
    text: nullableStr(
      'Prose that is not a building-part material, a catalog parameter, or one numeric limit, when kind=text. A facade or roof material does not go here. Null otherwise.',
    ),
    required: z
      .boolean()
      .nullable()
      .describe(
        'When kind=text: true for ska/måste/får inte, false for a goal or recommendation. Null otherwise.',
      ),
    comparison: z
      .nullable(buildingPartLimitComparisonSchema)
      .describe(
        'How the number compares: lt <, lte ≤ or högst, gt >, gte ≥ or minst, eq =. Null when kind is not limit.',
      ),
    number: nullableNum(
      'The limit as a number, in `unit`. Null when kind is not limit.',
    ),
    unit: nullableStr(
      'Unit as printed (m, mm, dBA). Null when kind is not limit.',
    ),
  })
  .strict();

export type BuildingPartConstraintAi = z.infer<
  typeof buildingPartConstraintAiSchema
>;

export const buildingPartMaterialConstraintSchema = z
  .object({
    kind: z.literal('material'),
    allowed: z.array(buildingPartMaterialSchema),
  })
  .strict();

export const buildingPartRangeConstraintSchema = z
  .object({
    kind: z.literal('range'),
    parameter_id: buildingPartRangeParameterIdSchema,
    min: z.nullable(z.number()),
    max: z.nullable(z.number()),
  })
  .strict();

export const buildingPartTextConstraintSchema = z
  .object({
    kind: z.literal('text'),
    text: z.string().min(1),
    required: z.boolean(),
  })
  .strict();

export const buildingPartLimitConstraintSchema = z
  .object({
    kind: z.literal('limit'),
    comparison: buildingPartLimitComparisonSchema,
    number: z.number(),
    unit: z.string().min(1),
  })
  .strict();

export const buildingPartConstraintSchema = z.discriminatedUnion('kind', [
  buildingPartMaterialConstraintSchema,
  buildingPartRangeConstraintSchema,
  buildingPartLimitConstraintSchema,
  buildingPartTextConstraintSchema,
]);

export type BuildingPartConstraint = z.infer<
  typeof buildingPartConstraintSchema
>;

const nameSchema = nullableStr(
  `On an element row, the label. When the document word matches one Nodon generic product, that product's name: ${genericMaterialProductList}. A match is that word, ignoring letter case, not a longer phrase that contains it. A word that does not match stays as the word. Not a product id. Not a list. Not a surface finish. Not a sentence, not a joined heading, and not a document-local name for a portion of the part. On a whole-building cap, the building the document names. Null when that cap names no building.`,
);
const materialSchema = nullableStr(
  'Null. An element row stores the label on `name`. A whole-building cap has no material.',
);
const legacyShareSchema = nullableStr(
  'Null on a whole-building cap. Not a portion of a building part.',
);

const printedMaterialStoredFields = {
  name: nameSchema.default(null),
  building_part: buildingPartPrintedCodesSchema.default(() => ({
    sbef: null,
    littra: null,
  })),
  material: materialSchema.default(null),
  share: legacyShareSchema.default(null),
};

const buildingPartRequirementSharedFields = {
  applies_to: z
    .enum(BUILDING_REQUIREMENT_APPLIES_TO)
    .describe(
      'building_part = one material on one building part (`type` element). building = a whole-building cap (max byggnadshöjd, våningsantal). A max building height is not an element row.',
    ),
  source_label: z
    .string()
    .describe(
      'Empty string on an element row. On a whole-building cap, the subject the document prints. Do not replace that subject. Not a sentence, not a quote, and not a joined heading.',
    ),
  typcode: z
    .nullable(typbeteckningCodeSchema)
    .describe(
      'Null. An element row does not store a typcode. A whole-building cap does not either. Do not choose a code from a part name or a recipe.',
    ),
  building_instance_key: nullableStr(
    "Null on an element row; that row uses `buildingId`. On a whole-building cap, that building. Use that building's classifier id when the list has it. Do not copy a different building's id onto this row. When the document names the building and the listed id is another building, the key is the building the document names, in the same form as a classifier id.",
  ),
  evidence_excerpt: z
    .string()
    .describe(
      'Empty string. Not a quote. A document-local name for a portion of a building part is not stored. A surface finish is not stored. The label is `name`. A stated share of the part is `share`.',
    ),
  confidence: z
    .number()
    .describe('0–1 certainty of this row; lower when the mapping is guessed.'),
};

const elementAiFields = {
  type: z
    .enum(['element'])
    .nullable()
    .describe(
      'element on a material row. Null on a whole-building cap. A max building height is not element.',
    ),
  buildingId: nullableStr(
    "The building an element row is about. Use that building's classifier id when the list has it. Do not copy a different building's id. When the document names the building and the listed id is another building, the id is the building the document names, in the same form as a classifier id. Null on a whole-building cap.",
  ),
  place: nullableStr(
    'Which building part on an element row. A load-bearing external wall is external_wall. Roof covering is roof. The cladding layer outside that wall is facade_cladding, not external_wall. Floor slabs are floor_slab. A frame that is not that wall is frame. A facade that is not the cladding layer is facade. A short name, not a sentence. Null on a whole-building cap.',
  ),
  name: nameSchema,
  share: z
    .number()
    .nullable()
    .describe(
      'The portion of that part, from 0 to 1. 1 is the whole part when the document does not split it. A fraction the document states. Null when the document splits the part and does not state the portions. Null on a whole-building cap. Do not invent a fraction. A count is not a portion.',
    ),
  sbef: nullableStr(
    'Element class on an element row. A load-bearing external wall is 31. Roof covering is 43. Facade cladding is 53. Floor slabs are 34 when the row says that subclass. When the row only names the family: 3 frame, 4 roof, 5 facades. A code the document prints is kept. Do not invent a second digit. Null on a whole-building cap.',
  ),
};

/**
 * Shape the extractor model emits. No `typcode_name`. An element row fills
 * `type`, `buildingId`, `place`, `name`, `share`, and `sbef`. A
 * whole-building cap leaves those null and fills `applies_to: building`.
 */
export const buildingPartRequirementAiSchema = z
  .object({
    ...buildingPartRequirementSharedFields,
    ...elementAiFields,
    constraint: buildingPartConstraintAiSchema,
  })
  .strict();

export type BuildingPartRequirementAi = z.infer<
  typeof buildingPartRequirementAiSchema
>;

export const buildingPartRequirementsAiArraySchema = z
  .array(buildingPartRequirementAiSchema)
  .describe(
    `${BUILDING_PART_MATERIAL_ROW_INSTRUCTION} Empty when the excerpt has none. Skip color, NCS, kulör.`,
  );

/**
 * A material on one building part. The stored row the baseline can use.
 * No typcode, littra, quote, or copy of `sbef`.
 */
export const elementRequirementSchema = z
  .object({
    type: z.literal('element'),
    buildingId: z.string().min(1),
    place: z.string().min(1),
    name: z.string().min(1),
    share: z.number().min(0).max(1).nullable(),
    sbef: z.string().min(1),
    constraint: z
      .object({
        kind: z.literal('material'),
        allowed: z.array(buildingPartMaterialSchema),
      })
      .strict(),
  })
  .strict();

export type ElementRequirement = z.infer<typeof elementRequirementSchema>;

/**
 * A whole-building cap, or a leftover range or limit the element row does
 * not represent. An element material is {@link elementRequirementSchema}.
 */
export const buildingPartRequirementSchema = z
  .object({
    ...buildingPartRequirementSharedFields,
    ...printedMaterialStoredFields,
    constraint: buildingPartConstraintSchema,
    typcode_name: z
      .nullable(z.string())
      .describe(
        'Mellankategori for typcode (Balk, Yttervägg); null if no code.',
      ),
    sbef_code: z
      .nullable(z.string())
      .describe(
        'Null on a whole-building cap. Not a lookup from a part name. An element row stores its code on `sbef` instead.',
      ),
  })
  .strict();

export type LegacyBuildingPartRequirement = z.infer<
  typeof buildingPartRequirementSchema
>;

export type BuildingPartRequirement =
  ElementRequirement | LegacyBuildingPartRequirement;

/** One segment extraction that backs an aggregated requirement. */
export const buildingPartRequirementSourceSchema = z.object({
  sourceFileId: z.string(),
  sourceFileName: z.string(),
  classificationSegmentIndex: z.number().int().nonnegative(),
  source_label: z.string(),
  evidence_excerpt: z.string(),
  confidence: z.number(),
});

export type BuildingPartRequirementSource = z.infer<
  typeof buildingPartRequirementSourceSchema
>;

/** A constraint value and every source that extracted exactly that value. */
export const buildingPartRequirementReadingSchema = z.object({
  constraint: buildingPartConstraintSchema,
  sources: z.array(buildingPartRequirementSourceSchema),
});

export type BuildingPartRequirementReading = z.infer<
  typeof buildingPartRequirementReadingSchema
>;

const aggregatedProvenanceFields = {
  sourceFileId: z.string(),
  sourceFileName: z.string(),
  classificationSegmentIndex: z.number().int().nonnegative(),
  /** Every extraction that agrees with `constraint`. */
  sources: z.array(buildingPartRequirementSourceSchema).optional(),
  /** Other values extracted for the same requirement; empty when all sources agree. */
  conflicts: z.array(buildingPartRequirementReadingSchema).optional(),
};

/**
 * One element row plus the file and segment it was read from.
 * `confidence` ranks disagreeing readings. It is not an element field.
 */
export const elementRequirementAggregatedSchema =
  elementRequirementSchema.extend({
    ...aggregatedProvenanceFields,
    confidence: z.number(),
  });

export type ElementRequirementAggregated = z.infer<
  typeof elementRequirementAggregatedSchema
>;

/**
 * One cap or leftover row per requirement identity. Top-level source fields
 * mirror the primary entry of `sources`. `sources` and `conflicts` are
 * absent on rows stored before sources were merged.
 */
export const legacyBuildingPartRequirementAggregatedSchema =
  buildingPartRequirementSchema.extend(aggregatedProvenanceFields);

/**
 * Element rows and whole-building caps in one batch list. An element row
 * does not parse as a cap, and a cap does not parse as an element row.
 */
export const buildingPartRequirementAggregatedSchema = z.union([
  elementRequirementAggregatedSchema,
  legacyBuildingPartRequirementAggregatedSchema,
]);

export type BuildingPartRequirementAggregated = z.infer<
  typeof buildingPartRequirementAggregatedSchema
>;
